import { User } from "@/entities/user";
import { setAdmin } from "@/entities/user/api";
import { useAuthStore } from "@/features/auth";
import { enqueueSnackbar } from "notistack";
import React, { useState, useEffect } from "react";
import { Container, Col, Row, Button, Badge } from "react-bootstrap";
import { formatPhone } from "@/shared/ui";
import { Chat, getChats } from "@/entities/chat";
import { Contact } from "@/entities/contact";
import './contacts.css';
import { ContactDetails } from "./contact-details";

export type UserWithPasswordFields = User & {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

const ContactsPage = () => {
  const { isAdmin } = useAuthStore();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [chats, setChats] = useState<Chat[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);


  const fetchChats = async (pageNum: number) => {
    if (loading) return;
    setLoading(true);

    try {
      const res = await getChats(pageNum);

      if (res.success) {
        setChats(prev => [...prev, ...res.result]);
        setCount(res.count || 0);
      } else {
        enqueueSnackbar("Не удалось загрузить контакты", { variant: "error" });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Ошибка при загрузке контактов", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChats(1);
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

  const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

  if (
    isBottom &&
    !loading &&
    chats.length < count
  ) {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchChats(nextPage);
  }
};

  const handleSetAdmin = async (id: string) => {
    if (id) return;

    try {
      const res = await setAdmin(id);
      if (res.success) {
        enqueueSnackbar("Пользователь назначен администратором", { variant: "success" });
      } else {
        enqueueSnackbar("Не удалось назначить администратора", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Ошибка при назначении администратора", { variant: "error" });
    }
  };

  const isAdminContact = (roles: string[]) => {
    return roles.includes("ADMIN")
  }

  return (
    <Container className="mt-4 contacts-page">
      <h2 className="page-title">Контакты</h2>

      <Row className="mt-4">
        <Col md={5} className="d-none d-md-block">
          <div className="contacts-card">
            <div  onScroll={handleScroll} className="contacts-list scrollbar-custom-y">
              {chats.map(chat => {
                const c = chat.contact;
                const u = c.user;

                const isSelected = selectedContact?._id === c._id;

                return (
                  <div
                    key={chat._id}
                    className={`contact-item ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedContact(c)
                        setSelectedChatId(chat._id)
                      }}
                  >
                    <div className="contact-name">{c.firstName} {c.lastName}</div>
                    <div className="contact-phone">{formatPhone(u.phone)}</div>

                    <div className="badges mt-2 d-flex gap-2">
                      <Badge bg={u.status === "REGISTERED" ? "success" : "warning"}>
                        {u.status === "REGISTERED" ? "Зарегистрирован" : "Приглашён"}
                      </Badge>

                      <Badge bg={isAdminContact(u.roles || []) ? "primary" : "secondary"}>
                        {isAdminContact(u.roles || []) ? "Админ" : "Пользователь"}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Col>

        <Col xs={12} className="d-md-none">
          {!selectedContact && (
            <div className="contacts-card">
              <div  onScroll={handleScroll} className="contacts-list scrollbar-custom-y">
                {chats.map(chat => {
                  const c = chat.contact;
                  const u = c.user;

                  return (
                    <div
                      key={chat._id}
                      className="contact-item"
                      onClick={() => {
                        setSelectedContact(c)
                        setSelectedChatId(chat._id)
                      }}
                    >
                      <div className="contact-name">{c.firstName} {c.lastName}</div>
                      <div className="contact-phone">{formatPhone(u.phone)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Col>


        <Col md={7} className="d-none d-md-block">
          <ContactDetails
            selectedContact={selectedContact}
            selectedChatId={selectedChatId}
            isAdmin={isAdmin}
            handleSetAdmin={handleSetAdmin}
          />
        </Col>

        <Col xs={12} className="d-md-none">
          {selectedContact && (
            <div className="contact-details-card">

              <Button
                variant="secondary"
                size="sm"
                className="mb-3"
                onClick={() => setSelectedContact(null)}
              >
                Назад
              </Button>

              <ContactDetails
                selectedContact={selectedContact}
                selectedChatId={selectedChatId}
                isAdmin={isAdmin}
                handleSetAdmin={handleSetAdmin}
              />
            </div>
          )}
        </Col>

      </Row>
    </Container>
  );

};

export default ContactsPage;